import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { youtubeThumb, youtubeEmbed } from "@/utils/youtube";
import { X, Play, Star } from "lucide-react";

const TABS = ["All", "Photos", "Videos"];
const byOrder = (a, b) => (a.order ?? 999) - (b.order ?? 999);

export default function Gallery() {
  const { site } = useSite();
  const [tab, setTab] = useState("All");
  const [selected, setSelected] = useState(null);

  const photos = [...site.gallery].sort(byOrder);
  const videos = [...site.videos].sort(byOrder);
  const showPhotos = tab !== "Videos";
  const showVideos = tab !== "Photos";
  const photoCats = ["All", ...new Set(site.gallery.map((g) => g.category))];

  return (
    <div data-testid="gallery-page">
      <PageHeader
        eyebrow="Bears In Action"
        title="Photos & Videos"
        subtitle={site.content.subtitle_gallery}
        testid="gallery-header"
      />

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex gap-2 flex-wrap mb-8" data-testid="gallery-type-tabs">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  data-testid={`gallery-tab-${t.toLowerCase()}`}
                  className={`px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wide transition-colors ${
                    tab === t ? "bg-[#0F4C2A] text-white shadow-md" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0F4C2A]/40"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Reveal>

          {showPhotos && (
            <>
              {tab === "All" && photos.length > 0 && videos.length > 0 && (
                <h2 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A] mb-5">Photos</h2>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
                {photos.map((p, i) => (
                  <Reveal key={p.id || p.src} delay={(i % 3) * 0.07}>
                    <button
                      className="gallery-tile group relative block w-full h-64 sm:h-72 rounded-lg overflow-hidden border border-slate-200 text-left"
                      onClick={() => setSelected({ type: "photo", ...p })}
                      data-testid={`gallery-item-${i}`}
                    >
                      <img src={p.src} alt={p.caption} loading="lazy" className="gallery-img h-full w-full object-cover" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-10">
                        <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#F59E0B]">{p.category}</span>
                        <span className="block text-sm font-semibold text-white mt-0.5">{p.caption}</span>
                      </div>
                    </button>
                  </Reveal>
                ))}
              </div>
              {photos.length === 0 && <p className="text-center text-slate-500 py-12">No photos yet — check back soon.</p>}
            </>
          )}

          {showVideos && (
            <div className={showPhotos ? "mt-12" : ""}>
              {tab === "All" && videos.length > 0 && photos.length > 0 && (
                <h2 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A] mb-5">Videos</h2>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="video-grid">
                {videos.map((v, i) => (
                  <Reveal key={v.id} delay={(i % 3) * 0.07}>
                    <button
                      className="gallery-tile group relative block w-full h-64 sm:h-72 rounded-lg overflow-hidden border border-slate-200 text-left"
                      onClick={() => setSelected({ type: "video", ...v })}
                      data-testid={`video-item-${i}`}
                    >
                      <img src={youtubeThumb(v.video_id)} alt={v.title} loading="lazy" className="gallery-img h-full w-full object-cover" />
                      <span className="absolute inset-0 bg-black/25 group-hover:bg-black/40 transition-colors" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="h-14 w-14 rounded-full bg-[#D97706] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <Play size={22} className="text-white fill-white ml-1" />
                        </span>
                      </span>
                      {v.featured && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#D97706] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
                          <Star size={11} className="fill-white" /> Featured
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4 pt-10">
                        <span className="block text-sm font-bold text-white">{v.title}</span>
                        {v.description && <span className="block text-xs text-white/75 mt-0.5 line-clamp-1">{v.description}</span>}
                      </div>
                    </button>
                  </Reveal>
                ))}
              </div>
              {videos.length === 0 && (
                <p className="text-center text-slate-500 py-12" data-testid="videos-empty">No videos yet — check back soon.</p>
              )}
            </div>
          )}
        </div>
      </section>

      {selected && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="gallery-lightbox"
        >
          <button className="absolute top-5 right-5 text-white/80 hover:text-white p-2" onClick={() => setSelected(null)} aria-label="Close" data-testid="lightbox-close">
            <X size={30} />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {selected.type === "video" ? (
              <div className="aspect-video rounded-md overflow-hidden bg-black" data-testid="lightbox-video">
                <iframe
                  src={youtubeEmbed(selected.video_id)}
                  title={selected.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <img src={selected.src} alt={selected.caption} className="w-full max-h-[75vh] object-contain rounded-md" data-testid="lightbox-image" />
            )}
            <p className="text-center text-white/85 text-sm mt-4" data-testid="lightbox-caption">
              {selected.type === "video" ? selected.title : selected.caption}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
